/* eslint-disable react/require-default-props */
import React, {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import { useWSContext } from '../../contexts/WebSocketContext';
import MensagemCliente from './MensagemCliente';
import MensagemSuporte from './MensagemSuporte';

const MESSAGE_ACTIONS = {
  ADD: 'ADD',
  CLEAR: 'CLEAR',
};

function messagesReducer(state, action) {
  switch (action.type) {
    case MESSAGE_ACTIONS.ADD: {
      const exists = state.some((msg) => msg.id === action.payload.id);
      return exists ? state : [...state, action.payload];
    }
    case MESSAGE_ACTIONS.CLEAR: {
      // console.log('✅ [Reducer] Ação CLEAR recebida. Resetando o estado das mensagens para [].');
      return [];
    }
    default:
      return state;
  }
}

function loadInitialState(initialValue = []) {
  try {
    const savedMessages = localStorage.getItem('chatHistory');
    if (savedMessages) {
      // console.log('📜 [Inicialização] Histórico encontrado no localStorage. Carregando mensagens.');
      return JSON.parse(savedMessages);
    }
    // console.log('📜 [Inicialização] Nenhum histórico encontrado no localStorage. Iniciando com estado vazio.');
    return initialValue;
  } catch (error) {
    // console.error('❌ Erro ao carregar mensagens do localStorage:', error);
    return initialValue;
  }
}

function AreaMensagem({
  addMensagemCliente = () => {},
  recebeEncerraChat = () => {},
  onReplyMessage = () => {},
}) {
  const {
    chatMessage: newChatMessage,
    chatResetCounter,
  } = useWSContext();

  const [chatMessages, dispatch] = useReducer(
    messagesReducer,
    [],
    loadInitialState
  );

  const isInitialMount = useRef(true); // Se precisar da lógica de novo atendimento

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaMessagesRef = useRef(new Map());

  const psContainerRef = useCallback((node) => {
    containerRef.current = node;
  }, []);

  useEffect(() => {
    if (chatMessages.length === 0) {
      // Se o array de mensagens estiver vazio, garante que o localStorage também esteja.
      if (localStorage.getItem('chatHistory')) {
        localStorage.removeItem('chatHistory');
        // console.log('🗑️ [Persistência] Estado de mensagens vazio. Histórico removido do localStorage.');
      }
    } else {
      try {
        localStorage.setItem('chatHistory', JSON.stringify(chatMessages));
        // console.log(`💾 [Persistência] ${chatMessages.length} mensagens salvas no localStorage.`);
      } catch (error) {
        // console.error('❌ Erro ao salvar mensagens no localStorage:', error);
      }
    }
  }, [chatMessages]);

  useEffect(() => {
    dispatch({ type: MESSAGE_ACTIONS.CLEAR });
    localStorage.removeItem('chatHistory');
  }, [chatResetCounter]);

  useEffect(() => {
    if (newChatMessage) {
      dispatch({ type: MESSAGE_ACTIONS.ADD, payload: newChatMessage });
    }
  }, [newChatMessage]);

  useEffect(() => {
    if (addMensagemCliente && typeof addMensagemCliente === 'object') {
      dispatch({ type: MESSAGE_ACTIONS.ADD, payload: addMensagemCliente });
    }
  }, [addMensagemCliente]);

  useLayoutEffect(() => {
    if (!chatMessages.length) return;
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  return (
    <PerfectScrollbar
      options={{ suppressScrollX: false }}
      containerRef={psContainerRef}
    >
      <Box id="areaMensagem" sx={{ p: 2 }}>
        {chatMessages.map((message) =>
          message.userType === 'suporte' ? (
            <MensagemSuporte key={message.id} message={message} onReply={onReplyMessage} />
          ) : (
            <MensagemCliente
              key={message.id}
              message={message}
              mediaMessagesRef={mediaMessagesRef}
              onReply={onReplyMessage}
            />
          )
        )}
        <Box ref={messagesEndRef} sx={{ mt: 2 }} />
      </Box>
    </PerfectScrollbar>
  );
}

AreaMensagem.propTypes = {
  addMensagemCliente: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
    PropTypes.string,
  ]),
  recebeEncerraChat: PropTypes.func,
  onReplyMessage: PropTypes.func,
};

export default React.memo(AreaMensagem);
